import React, { useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  MoreVertical,
  ChevronLeft,
  ChevronRight,
  FileX,
  ArrowDown,
  ArrowUp,
  ChevronsUpDown,
} from "lucide-react";
import { Borrower } from "@/types/borrower";
import { formatDate, formatDateTime } from "@/utils/dateUtils";
import { RootState } from "@/store/store";
import { borrowerDetailQueryOptions, useAppStatusTransitions } from "@/hooks/queries";
import { pluralize } from "@/utils/pluralize";
import { BorrowerDealMenuItems, useBorrowerDealActions } from "./BorrowerDealActions";
import { DefaultHistoryPill } from "./DefaultHistoryPill";
import { openApplicationDefaultHistory } from "./openApplication";
import { ContextMenu, ContextMenuContent, ContextMenuTrigger } from "@/components/ui/context-menu";
import AppStatusPill from "@/components/layout/AppStatusPill";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { shouldShowOrganizationColumn } from "@/utils/activeOrganization";
import { Badge } from "../ui/badge";

interface PaginationState {
  count: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

export type BorrowersTableSortColumn =
  | "company_name"
  | "broker_email"
  | "underwriter_email"
  | "created_at"
  | "last_status_changed_at";
export type BorrowersTableSortDirection = "asc" | "desc";
type BorrowersTableColumnKey =
  | BorrowersTableSortColumn
  | "status"
  | "default_history"
  | "organization";

interface BorrowersTableProps {
  borrowers: Borrower[];
  loading: boolean;
  pagination: PaginationState;
  page: number;
  pageSize: number;
  sortColumn: BorrowersTableSortColumn | null;
  sortDirection: BorrowersTableSortDirection;
  onRowClick: (borrowerId: number) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onSortClick: (column: BorrowersTableSortColumn) => void;
}

/** Converts a hex color into a tinted pill style — subtle bg, matching text, soft border. */
const BorrowersTable: React.FC<BorrowersTableProps> = ({
  borrowers,
  loading,
  pagination,
  page,
  pageSize,
  sortColumn,
  sortDirection,
  onRowClick,
  onPageChange,
  onPageSizeChange,
  onSortClick,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const prefetchedBorrowerIdsRef = useRef<Set<number>>(new Set());
  const prefetchBorrowerOnHover = useCallback(
    (borrowerId: number) => {
      if (prefetchedBorrowerIdsRef.current.has(borrowerId)) return;
      prefetchedBorrowerIdsRef.current.add(borrowerId);
      void queryClient.prefetchQuery(borrowerDetailQueryOptions(borrowerId));
    },
    [queryClient]
  );
  const user = useSelector((state: RootState) => state.auth.user);
  const readIds = useSelector((state: RootState) => state.borrowers.readIds);
  const isAdminOrUnderwriter =
    user?.role === "admin" || user?.role === "admin_support" || user?.role === "underwriter";
  const showOrganizationColumn = shouldShowOrganizationColumn(user);
  // Fetched once here and threaded down to every row's AppStatusPill, instead
  // of each row independently subscribing to the same query.
  const { choices: statusChoices } = useAppStatusTransitions();
  const dealActions = useBorrowerDealActions();

  const visibleColumnKeys: BorrowersTableColumnKey[] = isAdminOrUnderwriter
    ? [
        "company_name",
        ...(showOrganizationColumn ? (["organization"] as const) : []),
        "broker_email",
        "underwriter_email",
        "status",
        "default_history",
        "created_at",
        "last_status_changed_at",
      ]
    : showOrganizationColumn
      ? [
          "company_name",
          "organization",
          "status",
          "default_history",
          "created_at",
          "last_status_changed_at",
        ]
      : ["company_name", "status", "default_history", "created_at", "last_status_changed_at"];
  const columnCount = visibleColumnKeys.length;

  const handleDefaultHistoryClick = (e: React.MouseEvent, borrower: Borrower) => {
    e.stopPropagation();
    openApplicationDefaultHistory(navigate, borrower);
  };

  const sortColumnStripe = (columnKey: BorrowersTableColumnKey) =>
    sortColumn === columnKey ? "bg-slate-100" : "";

  const totalPages = pagination.totalPages || 1;
  const canPrev = page > 0;
  const canNext = page + 1 < totalPages;

  const rangeStart = pagination.count === 0 ? 0 : page * pagination.pageSize + 1;
  const rangeEnd = Math.min((page + 1) * pagination.pageSize, pagination.count);

  const renderSortableHeader = (
    label: string,
    column: BorrowersTableSortColumn,
    className: string,
    align: "left" | "center" = "left"
  ) => {
    const isActive = sortColumn === column;
    return (
      <TableHead className={cn(className, isActive && "bg-slate-100")}>
        <button
          type="button"
          className={cn(
            "inline-flex items-center gap-1 text-[12px] font-medium uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground",
            isActive && "text-foreground",
            align === "center" && "w-full justify-center"
          )}
          onClick={(e) => {
            e.stopPropagation();
            onSortClick(column);
          }}
        >
          {label}
          {isActive ? (
            sortDirection === "asc" ? (
              <ArrowUp className="h-3.5 w-3.5 shrink-0" />
            ) : (
              <ArrowDown className="h-3.5 w-3.5 shrink-0" />
            )
          ) : (
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
          )}
        </button>
      </TableHead>
    );
  };

  return (
    <>
      <Card className="overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow className="border-b bg-muted/30 hover:bg-muted/30">
              {renderSortableHeader("Company", "company_name", "w-[18%] pl-5")}
              {showOrganizationColumn && (
                <TableHead className="w-[14%] uppercase tracking-wide">Organization</TableHead>
              )}
              {isAdminOrUnderwriter && renderSortableHeader("Broker", "broker_email", "w-[14%]")}
              {isAdminOrUnderwriter &&
                renderSortableHeader("Underwriter", "underwriter_email", "w-[14%]")}
              <TableHead className="w-[13%] text-center uppercase tracking-wide">Status</TableHead>
              <TableHead className="w-[14%] text-center uppercase tracking-wide">
                Background Check
              </TableHead>
              {renderSortableHeader("Created", "created_at", "w-[11%] text-center", "center")}
              {renderSortableHeader(
                "Updated",
                "last_status_changed_at",
                "w-[11%] text-center",
                "center"
              )}
            </TableRow>
          </TableHeader>

          <TableBody>
            {loading ? (
              [...Array(6)].map((_, index) => (
                <TableRow key={index} className="border-b border-border/40">
                  {visibleColumnKeys.map((columnKey) => (
                    <TableCell key={columnKey} className={cn("py-4", sortColumnStripe(columnKey))}>
                      <Skeleton
                        className={cn("h-4", columnKey === "company_name" ? "w-40" : "w-full")}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : borrowers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnCount} className="py-16 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                      <FileX className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">No applications found</p>
                      <p className="mt-0.5 text-sm text-muted-foreground">
                        Use the New Application button above to get started.
                      </p>
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              borrowers.map((borrower, index) => {
                const isUnread = !borrower.is_read && !readIds.includes(borrower.id);
                const defaultCount = borrower.default_history_count ?? 0;
                // Cap the staggered fade-in so big page sizes don't pile up multi-second delays.
                const staggerIndex = Math.min(index, 12);

                return (
                  <ContextMenu key={borrower.id}>
                    <ContextMenuTrigger asChild>
                      <TableRow
                        className={cn(
                          "group cursor-pointer border-b border-border/40 transition-colors duration-100",
                          "animate-fade-in",
                          isUnread
                            ? "bg-background hover:bg-primary/5"
                            : "bg-slate-100/80 hover:bg-slate-100"
                        )}
                        style={{ animationDelay: `${staggerIndex * 22}ms` }}
                        onMouseEnter={() => prefetchBorrowerOnHover(borrower.id)}
                        onClick={(e) => dealActions.activateForTable(borrower, e, onRowClick)}
                        onAuxClick={(e) => {
                          if (e.button === 1) {
                            dealActions.activateForTable(borrower, e, onRowClick);
                          }
                        }}
                      >
                        {/* Company name — with unread left-border accent */}
                        <TableCell
                          className={cn(
                            "border-l-2 py-3.5 pl-4",
                            sortColumnStripe("company_name"),
                            isUnread ? "border-l-primary" : "border-l-transparent"
                          )}
                        >
                          <div className="flex items-start gap-2.5">
                            <div className="min-w-0">
                              <div
                                className={cn(
                                  "truncate text-[13px] leading-snug text-foreground",
                                  isUnread ? "font-semibold" : "font-medium"
                                )}
                              >
                                {borrower.company_name && borrower.company_name.length > 32
                                  ? borrower.company_name.slice(0, 18).replace(/\s\S*$/, "") + " …"
                                  : borrower.company_name}
                              </div>
                              {(borrower as any).owner_name && (
                                <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                                  {(borrower as any).owner_name}
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>

                        {showOrganizationColumn && (
                          <TableCell className="py-3.5 align-top">
                            <div className="truncate text-[13px] text-foreground">
                              {borrower.organization_name || "—"}
                            </div>
                          </TableCell>
                        )}

                        {/* Broker */}
                        {isAdminOrUnderwriter && (
                          <TableCell
                            className={cn("py-3.5 align-top", sortColumnStripe("broker_email"))}
                          >
                            <div className="truncate text-[13px] font-medium text-foreground">
                              {borrower.broker_name || borrower.broker_email || "—"}
                            </div>

                            <div className="mt-0.5 text-[11px]">
                              {borrower.broker_is_steddyuser === false ? (
                                <Badge
                                  // use a neutral / outline style instead of destructive red
                                  variant="outline"
                                  className="border-muted-foreground/30 bg-muted/40 text-muted-foreground"
                                >
                                  Not Steddy User
                                </Badge>
                              ) : (
                                borrower.broker_company_name && (
                                  <div className="truncate text-muted-foreground">
                                    {borrower.broker_company_name}
                                  </div>
                                )
                              )}
                            </div>
                          </TableCell>
                        )}

                        {/* Underwriter */}
                        {isAdminOrUnderwriter && (
                          <TableCell
                            className={cn(
                              "py-3.5 align-top",
                              sortColumnStripe("underwriter_email")
                            )}
                          >
                            <div className="truncate text-[13px] font-medium text-foreground">
                              {borrower.underwriter_name || borrower.underwriter_email ? (
                                borrower.underwriter_name || borrower.underwriter_email
                              ) : (
                                <span className="font-normal text-muted-foreground/40">—</span>
                              )}
                            </div>
                          </TableCell>
                        )}

                        {/* Status badge — icon + tinted pill */}
                        <TableCell className="py-3.5 text-center">
                          <AppStatusPill status={borrower.app_status} choices={statusChoices} />
                        </TableCell>

                        {/* Background check */}
                        <TableCell className="py-3.5 text-center">
                          {defaultCount > 0 ? (
                            <DefaultHistoryPill
                              onClick={(e) => handleDefaultHistoryClick(e, borrower)}
                            >
                              {defaultCount} {pluralize(defaultCount, "record")}
                            </DefaultHistoryPill>
                          ) : (
                            <span className="text-[13px] text-muted-foreground/40">—</span>
                          )}
                        </TableCell>

                        {/* Created */}
                        <TableCell
                          className={cn(
                            "py-3.5 text-center text-[12px] tabular-nums text-muted-foreground",
                            sortColumnStripe("created_at")
                          )}
                        >
                          <div className="flex flex-col items-center leading-snug">
                            <span className="text-foreground">
                              {formatDate(borrower.created_at)}
                            </span>
                            <span className="text-[11px] opacity-60">
                              {formatDateTime(borrower.created_at).split(", ").slice(-1)[0]}
                            </span>
                          </div>
                        </TableCell>

                        {/* Updated — action button overlays right edge on hover */}
                        <TableCell
                          className={cn(
                            "relative py-3.5 text-center text-[12px] tabular-nums text-muted-foreground",
                            sortColumnStripe("last_status_changed_at")
                          )}
                        >
                          <div className="flex flex-col items-center leading-snug">
                            <span className="text-foreground">
                              {formatDate(borrower.last_status_changed_at)}
                            </span>
                            <span className="text-[11px] opacity-60">
                              {
                                formatDateTime(borrower.last_status_changed_at)
                                  .split(", ")
                                  .slice(-1)[0]
                              }
                            </span>
                          </div>
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 opacity-0 transition-opacity group-hover:opacity-100 data-[state=open]:opacity-100"
                                  onClick={(e) => e.stopPropagation()}
                                  aria-label="Row actions"
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                                <BorrowerDealMenuItems
                                  borrower={borrower}
                                  variant="dropdown"
                                  canAssign={dealActions.canAssign}
                                  canDelete={dealActions.canDelete}
                                  onOpenNewTab={(b) => dealActions.open(b, { newTab: true })}
                                  onView={dealActions.view}
                                  onEdit={dealActions.edit}
                                  onAssign={dealActions.requestAssign}
                                  onDelete={dealActions.requestDelete}
                                />
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    </ContextMenuTrigger>
                    <ContextMenuContent onClick={(e) => e.stopPropagation()}>
                      <BorrowerDealMenuItems
                        borrower={borrower}
                        variant="context"
                        canAssign={dealActions.canAssign}
                        canDelete={dealActions.canDelete}
                        onOpenNewTab={(b) => dealActions.open(b, { newTab: true })}
                        onView={dealActions.view}
                        onEdit={dealActions.edit}
                        onAssign={dealActions.requestAssign}
                        onDelete={dealActions.requestDelete}
                      />
                    </ContextMenuContent>
                  </ContextMenu>
                );
              })
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {!loading && borrowers.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/50 bg-muted/20 px-5 py-3">
            <div className="flex flex-wrap items-center gap-4">
              <p className="text-[12px] text-muted-foreground">
                {rangeStart}–{rangeEnd} of{" "}
                <span className="font-medium text-foreground">{pagination.count}</span> applications
              </p>
              <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
                <span>Rows per page</span>
                <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
                  <SelectTrigger size="sm" className="w-[72px] bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[10, 20, 50, 100].map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                disabled={!canPrev}
                onClick={() => onPageChange(page - 1)}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="min-w-[90px] text-center text-[12px] text-muted-foreground">
                Page {page + 1} of {totalPages}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                disabled={!canNext}
                onClick={() => onPageChange(page + 1)}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {dealActions.dialogs}
    </>
  );
};

export default BorrowersTable;
